import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import logoImg from '../../logo.png';
import WhatsAppFloat from './WhatsAppFloat';
import { publicApi } from '../../services/api';
import { flattenSettings, getSocialLinks, getSettingValue, getThemeColors } from '../../utils/settings';
import { parseSectionContent } from '../../utils/sectionContent';

export default function Footer() {
  const [contact, setContact] = useState({ email: '', phone: '', address: '', whatsapp: '' });
  const [socialLinks, setSocialLinks] = useState([]);
  const [siteDescription, setSiteDescription] = useState('');
  const [siteName, setSiteName] = useState('Klinnovac');
  const [brandLogo, setBrandLogo] = useState(logoImg);
  const [theme, setTheme] = useState({ primary: '#081626', secondary: '#00CFFF' });

  useEffect(() => {
    Promise.all([
      publicApi.getSettings().catch(() => ({ data: { data: {} } })),
      publicApi.getPage('home').catch(() => ({ data: { data: { sections: [] } } })),
    ])
      .then(([settingsRes, homeRes]) => {
        const groups = settingsRes.data?.data || {};
        const flat = flattenSettings(groups);
        const sections = homeRes.data?.data?.sections || [];
        const brandingSection = sections.find(section => section.type === 'branding');
        const brandingContent = parseSectionContent(brandingSection?.content);
        setContact({
          email: flat.contact_email || 'info@klinnovac.com',
          phone: flat.contact_phone || '+62 878-6878-2477',
          address: flat.contact_address || 'Jl. Cemara Raya, Jakasampurna, Bekasi',
          whatsapp: flat.contact_whatsapp || '+62 878-6878-2477',
        });
        setSiteName(brandingContent.site_name || brandingContent.name || getSettingValue(flat, ['site_name', 'site_title'], 'Klinnovac'));
        setSiteDescription(flat.site_description || 'Klinnovac menghadirkan solusi pembersih premium dengan teknologi hydro vacuum terkini. Bersih, sehat, dan ramah lingkungan untuk rumah, kantor, dan kendaraan Anda.');
        setBrandLogo(brandingContent.logo_url || brandingContent.image_url || flat.site_logo || logoImg);
        setTheme(getThemeColors(flat));
        const social = getSocialLinks(flat);
        setSocialLinks(social.length ? social : [
          { icon: '📘', label: 'Facebook', href: '#' },
          { icon: '📸', label: 'Instagram', href: '#' },
          { icon: '▶️', label: 'YouTube', href: '#' },
          { icon: '🎵', label: 'TikTok', href: '#' },
          { icon: '🐦', label: 'Twitter', href: '#' },
        ]);
      })
      .catch(() => {});
  }, []);

  return (
    <>
      <footer style={{ backgroundColor: theme.primary }} className="text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src={brandLogo || logoImg} alt={siteName} className="h-10 w-auto object-contain" />
                <span className="text-xl font-bold" style={{ fontFamily: "'Cabin', sans-serif" }}>{siteName}</span>
              </div>
              <div
                className="text-brand-light text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: siteDescription }}
              />
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Tautan Cepat</h4>
              <ul className="space-y-2">
                {[
                  { to: '/', label: 'Beranda' },
                  { to: '/about', label: 'Tentang Kami' },
                  { to: '/products', label: 'Produk' },
                  { to: '/order', label: 'Pesan' },
                  { to: '/blog', label: 'Blog' },
                  { to: '/contact', label: 'Kontak' },
                ].map(link => (
                  <li key={link.to}>
                    <Link to={link.to} className="text-brand-light hover:text-brand transition-colors text-sm">{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Kontak</h4>
              <ul className="space-y-3 text-sm text-brand-light">
                {contact.email && <li className="flex items-start gap-2"><Mail size={16} className="mt-0.5 flex-shrink-0" /> <span>{contact.email}</span></li>}
                {contact.phone && <li className="flex items-start gap-2"><Phone size={16} className="mt-0.5 flex-shrink-0" /> <span>{contact.phone}</span></li>}
                {contact.address && <li className="flex items-start gap-2"><MapPin size={16} className="mt-0.5 flex-shrink-0" /> <span>{contact.address}</span></li>}
                {contact.whatsapp && <li className="flex items-start gap-2"><MessageCircle size={16} className="mt-0.5 flex-shrink-0" /> <span>WA: {contact.whatsapp}</span></li>}
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Ikuti Kami</h4>
              <div className="flex gap-3 flex-wrap">
                {socialLinks.map(social => (
                  <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-brand/20 transition-colors text-lg" title={social.label} style={{ backgroundColor: 'rgba(0,207,255,0.15)' }}>{social.icon}</a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>
      <WhatsAppFloat />
    </>
  );
}
